// client/src/pages/admin/ShopSettingsPage.js
import React, { useContext, useState, useEffect } from 'react';
import { Card, Form, Alert, Spinner, Button, Row, Col } from 'react-bootstrap';
import api from '../../api/axios';
import SettingsContext from '../../context/SettingsContext';
import AuthContext from '../../context/AuthContext';

const ShopSettingsPage = () => {
    const { settings, loading, updateSettings } = useContext(SettingsContext);
    const { loginWithToken } = useContext(AuthContext);
    const [isCarModelRequired, setIsCarModelRequired] = useState(true);
    const [workStartTime, setWorkStartTime] = useState('09:00');
    const [workEndTime, setWorkEndTime] = useState('18:00');
    const [businessNumber, setBusinessNumber] = useState('');
    const [bayCount, setBayCount] = useState(1);
    const [ownerName, setOwnerName] = useState('');
    const [email, setEmail] = useState('');
    const [phone, setPhone] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    useEffect(() => {
        if (settings) {
            setIsCarModelRequired(settings.is_car_model_required);
            setWorkStartTime(settings.work_start_time || '09:00');
            setWorkEndTime(settings.work_end_time || '18:00');
            setBusinessNumber(settings.business_number || '');
            setBayCount(settings.bay_count || 1);
            setOwnerName(settings.owner_name || '');
            setEmail(settings.email || '');
            setPhone(settings.phone || '');
        }
    }, [settings]);

    const handleToggle = async () => {
        const newValue = !isCarModelRequired;
        await saveSetting({ is_car_model_required: newValue });
        setIsCarModelRequired(newValue);
        updateSettings({ is_car_model_required: newValue });
    };

    const handleSaveWorkHours = async () => {
        await saveSetting({ work_start_time: workStartTime, work_end_time: workEndTime });
        updateSettings({ work_start_time: workStartTime, work_end_time: workEndTime });
    };

    const saveSetting = async (data) => {
        setIsSaving(true);
        setError('');
        setSuccess('');
        try {
            const res = await api.put('/shops/settings', data);
            const newAccessToken = res.data?.data?.accessToken;
            if (newAccessToken) {
                loginWithToken(newAccessToken);
            }
            setSuccess('설정이 성공적으로 저장되었습니다.');
        } catch (err) {
            setError('설정 저장에 실패했습니다.');
            console.error(err);
        } finally {
            setIsSaving(false);
        }
    };

    if (loading) {
        return <Spinner animation="border" />;
    }

    return (
        <>
            <div className="page-header">
                <div className="page-header-left">
                    <h3>매장 설정</h3>
                    <p>영업시간, 베이, 매장 기본 정보를 설정합니다</p>
                </div>
            </div>
            {error && <Alert variant="danger">{error}</Alert>}
            {success && <Alert variant="success">{success}</Alert>}

            <Card className="mb-3">
                <Card.Header>매장 기본 정보</Card.Header>
                <Card.Body>
                    <Row className="g-2 align-items-end mb-2">
                        <Col xs={6} sm={3}>
                            <Form.Group>
                                <Form.Label>대표자명</Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder="홍길동"
                                    value={ownerName}
                                    onChange={(e) => setOwnerName(e.target.value)}
                                    disabled={isSaving}
                                />
                            </Form.Group>
                        </Col>
                        <Col xs={6} sm={3}>
                            <Form.Group>
                                <Form.Label>이메일</Form.Label>
                                <Form.Control
                                    type="email"
                                    placeholder="email@example.com"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    disabled={isSaving}
                                />
                            </Form.Group>
                        </Col>
                        <Col xs={6} sm={3}>
                            <Form.Group>
                                <Form.Label>휴대폰 번호</Form.Label>
                                <Form.Control
                                    type="tel"
                                    placeholder="010-1234-5678"
                                    value={phone}
                                    onChange={(e) => setPhone(e.target.value)}
                                    disabled={isSaving}
                                />
                            </Form.Group>
                        </Col>
                        <Col xs={6} sm={3}>
                            <Form.Group>
                                <Form.Label>사업자등록번호</Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder="000-00-00000"
                                    value={businessNumber}
                                    onChange={(e) => setBusinessNumber(e.target.value)}
                                    disabled={isSaving}
                                />
                            </Form.Group>
                        </Col>
                        <Col xs={8} sm={2}>
                            <Form.Group>
                                <Form.Label>베이 수</Form.Label>
                                <Form.Select value={bayCount} onChange={(e) => setBayCount(Number(e.target.value))} disabled={isSaving}>
                                    {[1, 2, 3, 4].map(n => (
                                        <option key={n} value={n}>{n}개</option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                        </Col>
                        {/* 저장은 우측 끝. 좁은 칸에 두면 '저/장' 으로 줄바꿈되므로 text-nowrap 을 건다. */}
                        <Col xs={4} sm="auto" className="ms-sm-auto text-end">
                            <Button variant="primary" className="text-nowrap" onClick={async () => {
                                await saveSetting({ owner_name: ownerName, email, phone, business_number: businessNumber, bay_count: bayCount });
                                updateSettings({ owner_name: ownerName, email, phone, business_number: businessNumber, bay_count: bayCount });
                            }} disabled={isSaving}>
                                저장
                            </Button>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            <Card className="mb-3">
                <Card.Header>영업시간 설정</Card.Header>
                <Card.Body>
                    <Row className="align-items-end">
                        <Col xs={6} sm={4} md={3}>
                            <Form.Group>
                                <Form.Label>시작 시간</Form.Label>
                                <Form.Select
                                    value={workStartTime}
                                    onChange={(e) => setWorkStartTime(e.target.value)}
                                    disabled={isSaving}
                                >
                                    {Array.from({ length: 24 }, (_, h) => (
                                        <option key={h} value={`${String(h).padStart(2,'0')}:00`}>
                                            {String(h).padStart(2,'0')}:00
                                        </option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                        </Col>
                        <Col xs={6} sm={4} md={3}>
                            <Form.Group>
                                <Form.Label>종료 시간</Form.Label>
                                <Form.Select
                                    value={workEndTime}
                                    onChange={(e) => setWorkEndTime(e.target.value)}
                                    disabled={isSaving}
                                >
                                    {Array.from({ length: 24 }, (_, h) => (
                                        <option key={h} value={`${String(h).padStart(2,'0')}:00`}>
                                            {String(h).padStart(2,'0')}:00
                                        </option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                        </Col>
                        {/* 저장은 선택칸과 같은 줄에 두면 폭이 모자라 세로로 쪼개진다 — 한 줄 내려 우측 정렬. */}
                        <Col xs={12} className="text-end mt-2">
                            <Button variant="primary" className="text-nowrap" onClick={handleSaveWorkHours} disabled={isSaving}>
                                저장
                            </Button>
                        </Col>
                    </Row>
                    <Form.Text className="text-muted">
                        워크보드 스케줄러에 표시되는 영업시간을 설정합니다.
                    </Form.Text>
                </Card.Body>
            </Card>

            <Card>
                <Card.Header>차량 관리 설정</Card.Header>
                <Card.Body>
                    <Form.Group>
                        <Form.Check
                            type="switch"
                            id="car-model-required-switch"
                            label="차종(제조사·모델) 필수"
                            checked={isCarModelRequired}
                            onChange={handleToggle}
                            disabled={isSaving}
                        />
                        <Form.Text className="text-muted">
                            켜두면 차량 등록과 입고·완료 처리에 차종(제조사·모델)이 필요합니다.
                            예약은 차종 없이도 받을 수 있고, 차가 들어올 때 입력하면 됩니다.
                            끄면 어느 단계에서도 차종 없이 진행할 수 있습니다.
                        </Form.Text>
                    </Form.Group>
                </Card.Body>
            </Card>
        </>
    );
};

export default ShopSettingsPage;
